import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tradesperson } from '@/lib/types';
import { getPostcodeTradespeople, getPopularTradespeople, addUserTradesperson } from '@/lib/supabase/trades';
import { toast } from 'sonner';
import { Loader2, CheckCircle, Users, MapPin } from 'lucide-react';
import { getTradeIconForTrade, getTradeDisplayName, getTradeType } from '@/lib/tradeUtils';
import { useAuth } from '@/contexts/AuthContext';
import { debug } from '@/lib/utils';
import { trackEvent } from '@/lib/analytics';

type SelectedTradesState = Record<string, boolean>;

interface TradePersonSelectionStepProps {
  postcode: string;
  onComplete: () => void;
  onSkip: () => void;
}

export function TradePersonSelectionStep({ postcode, onComplete, onSkip }: TradePersonSelectionStepProps) {
  const { user } = useAuth();
  const [tradespeople, setTradespeople] = useState<Tradesperson[]>([]);
  const [selectedTrades, setSelectedTrades] = useState<SelectedTradesState>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [filteredTradespeople, setFilteredTradespeople] = useState<Tradesperson[]>([]);

  // Calculate if any tradespeople are selected
  const hasSelections = Object.values(selectedTrades).some(selected => selected);

  // Load saved selections from session storage
  useEffect(() => {
    const sessionKey = `tp_selection_${postcode}`;
    const savedSelections = sessionStorage.getItem(sessionKey);

    if (savedSelections) {
      try {
        const parsed = JSON.parse(savedSelections);
        setSelectedTrades(parsed);
      } catch (error) {
        debug.error('Error parsing saved selections:', error);
      }
    }
  }, [postcode]);

  // Save selections to session storage when they change
  useEffect(() => {
    if (Object.keys(selectedTrades).length > 0) {
      const sessionKey = `tp_selection_${postcode}`;
      sessionStorage.setItem(sessionKey, JSON.stringify(selectedTrades));
    }
  }, [selectedTrades, postcode]);

  // Load tradespeople for the user's postcode
  useEffect(() => {
    async function loadTradespeople() {
      setIsLoading(true);

      try {
        // Try to get tradespeople by postcode first
        let trades = await getPostcodeTradespeople(postcode, 30);

        // If we don't have enough results, supplement with popular tradespeople
        if (trades.length < 10) {
          const popularTrades = await getPopularTradespeople(20);

          // Filter out duplicates
          const existingIds = new Set(trades.map(t => t.id));
          const filteredPopular = popularTrades.filter(t => !existingIds.has(t.id));

          trades = [...trades, ...filteredPopular.slice(0, 20 - trades.length)];
        }

        // Extract unique categories
        const uniqueCategories = Array.from(
          new Set(trades.map(trade => getTradeType(trade)).filter(Boolean))
        );

        setTradespeople(trades);
        setFilteredTradespeople(trades);
        setCategories(uniqueCategories);
      } catch (error) {
        debug.error('Error loading tradespeople for selection:', error);
        toast.error('Failed to load recommended tradespeople');
      } finally {
        setIsLoading(false);
      }
    }

    loadTradespeople();
  }, [postcode]);

  // Filter tradespeople by category
  useEffect(() => {
    if (!activeCategory) {
      setFilteredTradespeople(tradespeople);
    } else {
      const filtered = tradespeople.filter(
        trade => getTradeType(trade) === activeCategory
      );
      setFilteredTradespeople(filtered);
    }
  }, [activeCategory, tradespeople]);

  // Toggle selection for a tradesperson
  const toggleSelection = (id: string) => {
    const newState = !selectedTrades[id];
    setSelectedTrades(prev => ({
      ...prev,
      [id]: newState
    }));

    // Track selection events
    const tradeperson = tradespeople.find(t => t.id === id);
    if (tradeperson) {
      trackEvent(newState ? 'tradesperson_selected' : 'tradesperson_deselected', {
        trade_type: getTradeType(tradeperson),
        postcode: postcode,
        has_phone: !!tradeperson.phone,
        has_email: !!tradeperson.email,
        verified: !!tradeperson.verified,
      });
    }
  };

  // Select a category filter
  const selectCategory = (category: string | null) => {
    const newCategory = activeCategory === category ? null : category;
    setActiveCategory(newCategory);

    // Track category filter usage
    trackEvent('tradesperson_category_filter', {
      category: category,
      action: newCategory ? 'apply' : 'clear',
      postcode: postcode,
    });
  };

  // Select/deselect all tradespeople in a category
  const toggleCategorySelection = (category: string) => {
    // Find all tradespeople in this category
    const tradesInCategory = tradespeople.filter(
      trade => getTradeType(trade) === category
    );

    // Check if all are currently selected
    const allSelected = tradesInCategory.every(trade => selectedTrades[trade.id]);
    const newSelectionState = !allSelected;

    // Toggle selection based on current state
    const newSelections = { ...selectedTrades };

    tradesInCategory.forEach(trade => {
      newSelections[trade.id] = newSelectionState;
    });

    setSelectedTrades(newSelections);

    // Track bulk selection
    trackEvent('tradesperson_bulk_selection', {
      category: category,
      action: newSelectionState ? 'select_all' : 'deselect_all',
      count: tradesInCategory.length,
      postcode: postcode,
    });
  };

  // Save selected tradespeople to user's account
  const saveSelectedTradespeople = async () => {
    if (!user) {
      toast.error('You must be logged in to save tradespeople');
      return;
    }

    const selectedIds = Object.entries(selectedTrades)
      .filter(([_, selected]) => selected)
      .map(([id]) => id);

    if (selectedIds.length === 0) {
      onComplete();
      return;
    }

    setIsSaving(true);

    try {
      // Add each selected tradesperson to the user's list
      const promises = selectedIds.map(id =>
        addUserTradesperson(user.id, id, {
          notes: `Added during signup from ${postcode.toUpperCase()}`,
          preferred: false
        })
      );

      await Promise.all(promises);

      // Clear the session storage after successful save
      const sessionKey = `tp_selection_${postcode}`;
      sessionStorage.removeItem(sessionKey);

      // Track successful save with analytics
      trackEvent('tradesperson_selection_saved', {
        count: selectedIds.length,
        postcode: postcode,
        categories: Array.from(new Set(
          selectedIds.map(id => {
            const trade = tradespeople.find(t => t.id === id);
            return trade ? getTradeType(trade) : 'unknown';
          })
        )).join(','),
      });

      toast.success(`${selectedIds.length} tradespeople added to your list!`);
      onComplete();
    } catch (error) {
      debug.error('Error saving selected tradespeople:', error);
      toast.error('Failed to save some tradespeople');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="w-full border-2 border-purple-900 rounded-xl shadow-lg">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center" style={{ color: "#5D4A9C" }}>
          Add Local Tradespeople
        </CardTitle>
        <CardDescription className="text-center text-purple-700">
          Select trusted tradespeople in your area to add to your list
        </CardDescription>
      </CardHeader>

      <CardContent>
        {/* Category filter */}
        {categories.length > 0 && (
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-purple-800">Filter by Trade Type</h3>
              {activeCategory && (
                <button
                  onClick={() => toggleCategorySelection(activeCategory)}
                  className="text-xs text-purple-700 hover:text-purple-900 underline"
                >
                  {filteredTradespeople.every(trade => selectedTrades[trade.id])
                    ? "Deselect All"
                    : "Select All"}
                </button>
              )}
            </div>
            <div className="overflow-x-auto pb-2">
              <div className="flex space-x-2 min-w-max">
                {categories.map(category => {
                  const TradeIcon = getTradeIconForTrade({ trade_type: category } as Tradesperson);
                  const isActive = activeCategory === category;

                  // Count how many tradespeople in this category are selected
                  const categoryTrades = tradespeople.filter(t => getTradeType(t) === category);
                  const selectedCount = categoryTrades.filter(t => selectedTrades[t.id]).length;
                  const hasSelections = selectedCount > 0;

                  return (
                    <button
                      key={category}
                      onClick={() => selectCategory(category)}
                      className={`flex flex-col items-center p-2 rounded-lg transition-colors relative ${
                        isActive
                          ? 'bg-purple-200 text-purple-900'
                          : hasSelections
                            ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-purple-100'
                      }`}
                      aria-label={isActive ? `Clear ${category} filter` : `Filter by ${category}`}
                    >
                      <TradeIcon className="h-5 w-5 mb-1" />
                      <span className="text-xs font-medium whitespace-nowrap">
                        {category}
                      </span>

                      {/* Show selection count indicator */}
                      {hasSelections && (
                        <div className="absolute -top-1 -right-1 bg-purple-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {selectedCount}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Loading state */}
        {isLoading ? (
          <div className="py-8 flex justify-center items-center">
            <Loader2 className="h-8 w-8 text-purple-700 animate-spin" />
            <p className="ml-3 text-purple-700">Finding tradespeople in your area...</p>
          </div>
        ) : filteredTradespeople.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-lg text-purple-700 mb-2">No tradespeople found</p>
            <p className="text-sm text-purple-600">
              {activeCategory
                ? `No ${activeCategory} found in your area. Try another category or continue to add manually later.`
                : `We couldn't find tradespeople in your area yet. You can add them manually later.`
              }
            </p>
          </div>
        ) : (
          <div className="space-y-3 mb-5 max-h-[400px] overflow-y-auto p-1">
            {filteredTradespeople.map(trade => (
              <TradePersonSelectionCard
                key={trade.id}
                trade={trade}
                isSelected={!!selectedTrades[trade.id]}
                onToggle={() => toggleSelection(trade.id)}
              />
            ))}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-col space-y-3 mt-8">
          <Button
            onClick={saveSelectedTradespeople}
            disabled={isSaving || (!hasSelections && !isLoading)}
            className="w-full bg-purple-700 hover:bg-purple-800 py-2.5 text-base font-medium"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving selections...
              </>
            ) : hasSelections ? (
              <>
                <CheckCircle className="mr-2 h-5 w-5" />
                Add {Object.values(selectedTrades).filter(Boolean).length} selected
              </>
            ) : (
              'Continue'
            )}
          </Button>

          <Button
            onClick={() => {
              // Clear session storage when skipping
              const sessionKey = `tp_selection_${postcode}`;
              sessionStorage.removeItem(sessionKey);

              // Track skip event
              trackEvent('tradesperson_selection_skipped', {
                postcode: postcode,
                available_tradespeople: tradespeople.length,
                filtered_count: filteredTradespeople.length,
                had_selections: Object.values(selectedTrades).some(Boolean),
              });

              onSkip();
            }}
            variant="outline"
            className="w-full border-gray-300 text-gray-700 hover:bg-gray-100"
            disabled={isSaving}
          >
            Skip for now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Selection card component
interface TradePersonSelectionCardProps {
  trade: Tradesperson;
  isSelected: boolean;
  onToggle: () => void;
}

function TradePersonSelectionCard({ trade, isSelected, onToggle }: TradePersonSelectionCardProps) {
  const displayName = getTradeDisplayName(trade);
  const tradeType = getTradeType(trade);
  const TradeIcon = getTradeIconForTrade(trade);

  return (
    <div
      className={`p-3 border-2 rounded-lg transition-colors cursor-pointer ${
        isSelected
          ? 'border-purple-500 bg-purple-50'
          : 'border-gray-200 bg-white hover:bg-gray-50'
      }`}
      onClick={onToggle}
    >
      <div className="flex items-center">
        {/* Selection indicator and trade icon */}
        <div className={`rounded-lg p-2 mr-3 ${isSelected ? 'bg-purple-200' : 'bg-gray-100'}`}>
          <TradeIcon className={`h-5 w-5 ${isSelected ? 'text-purple-700' : 'text-gray-600'}`} />
        </div>

        {/* Trade details */}
        <div className="flex-1">
          <h3 className={`font-medium ${isSelected ? 'text-purple-900' : 'text-gray-900'}`}>
            {displayName}
          </h3>
          <p className={`text-sm ${isSelected ? 'text-purple-700' : 'text-gray-600'}`}>
            {tradeType}
          </p>
        </div>

        {/* Selection checkbox */}
        <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${
          isSelected
            ? 'border-purple-500 bg-purple-500'
            : 'border-gray-300'
        }`}>
          {isSelected && <CheckCircle className="h-5 w-5 text-white" />}
        </div>
      </div>

      {/* Additional info badges */}
      <div className="flex mt-2 text-xs">
        {trade.postcode && (
          <div className={`flex items-center mr-3 ${isSelected ? 'text-purple-600' : 'text-gray-500'}`}>
            <MapPin className="h-3 w-3 mr-1" />
            {trade.postcode.toUpperCase()}
          </div>
        )}

        {Number(trade.recommendation_count || 0) > 0 && (
          <div className={`flex items-center ${isSelected ? 'text-purple-600' : 'text-gray-500'}`}>
            <Users className="h-3 w-3 mr-1" />
            {trade.recommendation_count} {trade.recommendation_count === 1 ? 'recommendation' : 'recommendations'}
          </div>
        )}
      </div>
    </div>
  );
}
